import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Creator, Video } from '../types';
import { PlusIcon } from '../components/Icons';

interface DashboardProps {
  creators: Creator[];
  recentVideos: Video[];
}

const Dashboard: React.FC<DashboardProps> = ({ creators, recentVideos }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-8">
      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">{t('dashboard.yourCreators')}</h2>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-4 [-ms-scrollbar-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {creators.map((creator) => (
            <Link to={`/creator/${creator.id}`} key={creator.id} className="flex flex-col items-center gap-2 text-center flex-shrink-0 w-24">
              <div className="relative">
                <img alt={creator.name} className="h-24 w-24 rounded-full object-cover ring-2 ring-white dark:ring-gray-800 shadow-md" src={creator.avatarUrl} />
                {creator.isLive && (
                  <div className="absolute top-0 right-1">
                     <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                      </span>
                  </div>
                )}
              </div>
              <p className="text-sm font-medium w-full truncate text-gray-800 dark:text-gray-200">{creator.name}</p>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xl font-bold tracking-tight mb-4 text-gray-900 dark:text-gray-100">{t('dashboard.recentVideos')}</h2>
        <div className="space-y-4">
          {recentVideos.map((video) => (
            <div key={video.id} className="flex items-start gap-4 rounded-xl bg-white dark:bg-gray-800 p-4 shadow-sm transition-shadow hover:shadow-md">
              <div className="w-32 flex-shrink-0">
                <div className="aspect-video w-full overflow-hidden rounded-lg bg-gray-200 dark:bg-gray-700">
                  <img alt={video.title} className="h-full w-full object-cover" src={video.thumbnailUrl} />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-[var(--primary-color)]">{video.creatorName}</p>
                <h3 className="mt-1 font-bold leading-tight text-gray-800 dark:text-gray-200">{video.title}</h3>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('dashboard.videoMeta', { views: video.views, uploadedAt: video.uploadedAt })}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;